import { useMemo } from 'react';
import { Employee } from '@/types';
import { isSlovakCompliantEmployee, validateSlovakEmployee, getSlovakComplianceCompleteness } from '@/lib/employees';
import { isPayrollEligible } from '@/lib/employees/contract-status';
import { useEmployees } from './useEmployees';

interface UseSlovakEmployeesProps {
  businessId: string | null;
  includeAll?: boolean; // If true, include all employees with Slovak compliance status
}

interface SlovakEmployeeData extends Employee {
  slovakCompliant: boolean;
  complianceCompleteness: number;
  complianceValid: boolean;
  complianceErrors: string[];
  complianceWarnings: string[];
}

interface UseSlovakEmployeesReturn {
  slovakEmployees: SlovakEmployeeData[];
  allEmployeesWithStatus: SlovakEmployeeData[];
  loadingSlovakEmployees: boolean;
  refreshSlovakEmployees: () => void;
}

export const useSlovakEmployees = ({
  businessId,
  includeAll = false
}: UseSlovakEmployeesProps): UseSlovakEmployeesReturn => {
  // Use the shared useEmployees hook instead of duplicate database calls
  const { employees, loadingEmployees, refetch } = useEmployees({ businessId });

  // Transform employees with Slovak compliance data using memoization for performance
  const allEmployeesWithStatus = useMemo(() => {
    return employees.map(employee => {
      const slovakCompliant = isSlovakCompliantEmployee(employee);
      const complianceCompleteness = getSlovakComplianceCompleteness(employee);
      const validation = validateSlovakEmployee(employee);

      return {
        ...employee,
        slovakCompliant,
        complianceCompleteness,
        complianceValid: validation.valid,
        complianceErrors: validation.errors,
        complianceWarnings: validation.warnings
      } as SlovakEmployeeData;
    });
  }, [employees]);

  // Filter only Slovak-compliant AND payroll-eligible employees
  const slovakEmployees = useMemo(() => {
    return allEmployeesWithStatus.filter(emp =>
      emp.slovakCompliant && isPayrollEligible(emp.contract_status || 'active', emp.contract_type)
    );
  }, [allEmployeesWithStatus]);

  return {
    slovakEmployees,
    allEmployeesWithStatus: includeAll ? allEmployeesWithStatus : slovakEmployees,
    loadingSlovakEmployees: loadingEmployees,
    refreshSlovakEmployees: refetch,
  };
};

/**
 * Hook to get employees that need Slovak compliance setup
 */
export const useEmployeesNeedingSlovakSetup = (businessId: string | null) => {
  const { allEmployeesWithStatus, loadingSlovakEmployees } = useSlovakEmployees({ 
    businessId, 
    includeAll: true 
  });

  const employeesNeedingSetup = allEmployeesWithStatus.filter(emp => 
    !emp.slovakCompliant || emp.complianceCompleteness < 80
  );

  return {
    employeesNeedingSetup,
    needingSetupCount: employeesNeedingSetup.length,
    loading: loadingSlovakEmployees
  };
};

/**
 * Hook to get specific employee with Slovak compliance status
 */
export const useEmployeeSlovakStatus = (employeeId: string | null, businessId: string | null) => {
  const { allEmployeesWithStatus, loadingSlovakEmployees } = useSlovakEmployees({ 
    businessId, 
    includeAll: true 
  });

  const employee = employeeId 
    ? allEmployeesWithStatus.find(emp => emp.id === employeeId)
    : null;

  return {
    employee,
    loading: loadingSlovakEmployees,
    slovakCompliant: employee?.slovakCompliant || false,
    complianceCompleteness: employee?.complianceCompleteness || 0,
    complianceValid: employee?.complianceValid || false,
    complianceErrors: employee?.complianceErrors || [],
    complianceWarnings: employee?.complianceWarnings || []
  };
};